import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Award, Sparkles, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

/**
 * <RecognitionSlider /> — futuristic auto-rotating award/recognition slider,
 * shown just below the hero. Content lives in the `recognitions` CMS.
 * Each item can specify its own display duration in ms.
 */
export default function RecognitionSlider() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/recognitions").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const dur = items[idx]?.duration_ms || 5000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % items.length), dur);
    return () => clearTimeout(t);
  }, [idx, items]);

  if (!items.length) return null;
  const cur = items[idx];
  const Wrap = cur.link ? "a" : "div";

  return (
    <section className="py-10 md:py-14" data-testid="home-recognition">
      <div className="max-w-6xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-[28px] border border-primary/25 bg-gradient-to-br from-primary/[0.08] via-background to-secondary/[0.06] shadow-[0_20px_60px_-20px_hsl(var(--primary)/0.35)]">
          {/* animated grid backdrop */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(hsl(var(--primary)/0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)/0.4) 1px, transparent 1px)",
              backgroundSize: "42px 42px",
              maskImage: "radial-gradient(ellipse at center, black 55%, transparent 100%)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/20 blur-3xl pointer-events-none"
          />

          <Wrap
            {...(cur.link ? { href: cur.link, target: "_blank", rel: "noopener noreferrer" } : {})}
            className="relative flex flex-col md:flex-row items-stretch md:items-center gap-6 p-6 md:p-8 group"
          >
            <div className="flex items-center gap-4 shrink-0">
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
                  {cur.image ? (
                    <img src={cur.image} alt={cur.title} className="w-10 h-10 object-contain" />
                  ) : (
                    <Award className="w-7 h-7" strokeWidth={1.7} />
                  )}
                </div>
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-2xl border border-primary/50 animate-ping"
                  style={{ animationDuration: "3.5s" }}
                />
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-primary">
                <Sparkles className="w-3.5 h-3.5" /> Recognition
              </div>
            </div>

            <div className="flex-1 min-w-0 relative min-h-[64px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={cur.id || idx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  data-testid={`recognition-slide-${idx}`}
                >
                  <div className="font-heading font-bold text-xl md:text-2xl leading-tight">
                    {cur.title}
                  </div>
                  {cur.description && (
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {cur.description}
                    </p>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {cur.link && (
              <div className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowUpRight className="w-4 h-4" />
              </div>
            )}
          </Wrap>

          {/* progress dots */}
          {items.length > 1 && (
            <div className="relative pb-4 flex items-center justify-center gap-1.5">
              {items.map((it, i) => (
                <button
                  key={it.id || i}
                  onClick={() => setIdx(i)}
                  aria-label={`Show recognition ${i + 1}`}
                  data-testid={`recognition-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-primary" : "w-4 bg-primary/25 hover:bg-primary/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
